import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getAuditStatus, getAuditReport,
  getProductPrefill, getChannelContent,
  getAuditVersions, processAuditQueue,
  restartAudit,
} from '@/lib/api/amazon-listing'
import type { AuditVersion, AuditRestartResult } from '@/lib/api/amazon-listing'

export function useAuditStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['amazon-audit', 'status', jobId],
    queryFn: () => getAuditStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      if (query.state.error) return false
      const status = query.state.data?.status
      return status === 'completed' || status === 'failed' ? false : 3000
    },
    retry: 1,
  })
}

export function useAuditReport(jobId: string | null) {
  return useQuery({
    queryKey: ['amazon-audit', 'report', jobId],
    queryFn: () => getAuditReport(jobId!),
    enabled: !!jobId,
    staleTime: 60_000,
  })
}

export function useProductPrefill(productId: string | null) {
  return useQuery({
    queryKey: ['product-prefill', productId],
    queryFn: () => getProductPrefill(productId!),
    enabled: !!productId,
  })
}

export function useChannelContent(productId: string | null, channelId: string | null) {
  return useQuery({
    queryKey: ['channel-content', productId, channelId],
    queryFn: () => getChannelContent(productId!, channelId!),
    enabled: !!productId && !!channelId,
  })
}

export function useAuditVersions(productId: string | null) {
  return useQuery({
    queryKey: ['amazon-audit', 'versions', productId],
    queryFn: () => getAuditVersions(productId!),
    enabled: !!productId,
  })
}

export function useProcessQueue() {
  const fired = useRef(false)
  const { mutate } = useMutation({ mutationFn: processAuditQueue })

  useEffect(() => {
    if (!fired.current) {
      fired.current = true
      mutate()
    }
  }, [mutate])
}

export function useRestartAudit() {
  const queryClient = useQueryClient()
  return useMutation<AuditRestartResult, Error, string>({
    mutationFn: (jobId: string) => restartAudit(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['amazon-audit'] })
    },
  })
}

export type { AuditVersion }
